import * as Complex from './complex.js';
import * as ESeries from './e-series.js';

// Library internals - used by exported methods of index.js to do the bulk of the work

export const NEARLY_INFINITY = 1e66;
export const INFINITY = 1e66;

//Computes the theoretical response of an ideal filter at 1 frequency
//Give it the natural frequency, gain, q, order, type, and a frequency.  It will return then return the filter's magnitude at that frequency.
export let responseCalculators = {
  lowpass: [
    // first is first-order filter, second is for second-order filter - using array here lets us index by numeric (order-1).
    function (fp, gain, q, f) {
      let complexGain = Complex.createComplex(gain, 0);
      return complexGain.divide(Complex.createComplex(1, f / fp));
    },
    function (fp, gain, q, f) {
      let complexGain = Complex.createComplex(gain, 0);
      return complexGain.divide(Complex.createComplex((fp * fp - f * f) / (fp * fp), (f * fp) / q / (fp * fp)));
    },
  ],
  highpass: [
    function (fp, gain, q, f) {
      let complexGain = Complex.createComplex(gain, 0);
      return Complex.createComplex(0, (complexGain.real * f) / fp).divide(Complex.createComplex(1, f / fp));
    },
    function (fp, gain, q, f) {
      let complexGain = Complex.createComplex(gain, 0);
      return complexGain.divide(Complex.createComplex((fp * fp - f * f) / (-1 * f * f), (f * fp) / q / (-1 * f * f)));
    },
  ],
  bandpass: [
    undefined,
    function (fp, gain, q, f) {
      let complexGain = Complex.createComplex(gain, 0);
      return complexGain
        .mult(Complex.createComplex(0, (f * fp) / q))
        .divide(Complex.createComplex(fp * fp - f * f, (f * fp) / q));
    },
  ],
};

//calculates corresponding cGov for a given component.  We use this to determine the min and max cGov for each components, which then allows us to
//determine the allowed cGov for the entire circuit
//exported only for internals consistency testing - not for public consumption
export let governingComponentCalculators = {
  singleEnded: {
    passiveFirstOrder: {
      lowpass: {
        // Governing component whose value is being returned: C1
        R1: function (r1, stageFp) {
          return 1 / (stageFp * 2 * Math.PI * r1);
        },
      },
      highpass: {
        // Governing component whose value is being returned: C1
        R1: function (r1, stageFp /*, stageQ, gain*/) {
          return 1 / (stageFp * 2 * Math.PI * r1);
        },
      },
    },
    activeFirstOrder: {
      lowpass: {
        // Governing component whose value is being returned: C1
        R1: function (r1, stageFp, _stageQ, gain) {
          if (gain > 0) {
            return 1 / (stageFp * 2 * Math.PI * r1);
          } else {
            return 1 / (stageFp * 2 * Math.PI * r1 * Math.abs(gain));
          }
        },
        R2: function (r2, stageFp /*, stageQ, gain*/) {
          //This should only trigger for negative gain cases, as positive gain should not ever have R2
          return 1 / (stageFp * 2 * Math.PI * r2);
        },
      },
      highpass: {
        // Governing component whose value is being returned: C1
        R1: function (r1, stageFp /*, stageQ, gain*/) {
          return 1 / (stageFp * 2 * Math.PI * r1);
        },
        R2: function (r2, stageFp, _stageQ, gain) {
          if (gain > 0) {
            return undefined;
          } else {
            return 1 / (stageFp * 2 * Math.PI * (r2 / Math.abs(gain)));
          }
        },
      },
    },
    passiveInductor: {
      lowpass: {
        // Governing component whose value is being returned: C1
        R1: function (r1, stageFp, stageQ /* gain*/) {
          let w0 = stageFp * 2 * Math.PI;
          return 1 / (w0 * stageQ * r1);
        },
        L1: function (l1, stageFp /*, stageQ, gain*/) {
          let w0 = stageFp * 2 * Math.PI;
          return 1 / (w0 * w0 * l1);
        },
      },
      bandpass: {
        // Governing component whose value is being returned: C1
        R1: function (r1, stageFp, stageQ /*, gain*/) {
          let w0 = stageFp * 2 * Math.PI;
          return stageQ / (w0 * r1);
        },
        L1: function (l1, stageFp /*, _stageQ, gain*/) {
          let w0 = stageFp * 2 * Math.PI;
          return 1 / (w0 * w0 * l1);
        },
      },
    },
    activeInductor: {
      lowpass: {
        // Governing component whose value is being returned: C1
        R1: function (r1, stageFp, stageQ /* gain*/) {
          let w0 = stageFp * 2 * Math.PI;
          return 1 / (w0 * stageQ * r1);
        },
        L1: function (l1, stageFp /*, stageQ, gain*/) {
          let w0 = stageFp * 2 * Math.PI;
          return 1 / (w0 * w0 * l1);
        },
      },
      // highpass: {
      // r1: function (cGov, stageFp, stageQ, gain, rGain) {
      //     let w0 = stageFp * 2 * Math.PI;
      //     return 1 / (w0 * stageQ * cGov);
      // },
      // l1: function (cGov, stageFp, stageQ, gain, rGain) {
      //     let w0 = stageFp * 2 * Math.PI;
      //     return 1 / (w0 * w0 * cGov);
      // }
      // }
      bandpass: {
        // Governing component whose value is being returned: C1
        R1: function (r1, stageFp, stageQ /*, gain*/) {
          let w0 = stageFp * 2 * Math.PI;
          return stageQ / (w0 * r1);
        },
        L1: function (l1, stageFp /*, _stageQ, gain*/) {
          let w0 = stageFp * 2 * Math.PI;
          return 1 / (w0 * w0 * l1);
        },
      },
    },
    multipleFeedback: {
      lowpass: {
        // Governing component whose value is being returned: C2
        C1: function (c1, stageFp, stageQ, gain) {
          let alpha = 1 / stageQ;
          return c1 / ((4 / (alpha * alpha)) * (gain + 1));
        },
        R2pR5: function (parallelR2R5, stageFp, stageQ, gain) {
          let w0 = stageFp * 2 * Math.PI;
          let alpha = 1 / stageQ;
          return alpha / (2 * w0 * parallelR2R5 * (gain + 2));
        },
        R1: function (r1, stageFp, stageQ, gain) {
          let w0 = stageFp * 2 * Math.PI;
          let alpha = 1 / stageQ;
          return alpha / (2 * gain * w0 * r1);
        },
        R2: function (r2, stageFp, stageQ, gain) {
          let w0 = stageFp * 2 * Math.PI;
          let alpha = 1 / stageQ;
          return alpha / (2 * (gain + 1) * w0 * r2);
        },
        R5: function (r5, stageFp, stageQ /*, gain*/) {
          let w0 = stageFp * 2 * Math.PI;
          let alpha = 1 / stageQ;
          return alpha / (2 * w0 * r5);
        },
      },
      highpass: {
        // Governing component whose value is being returned: C1
        C2: function (c2 /*, stageFp, stageQ, gain*/) {
          return c2;
        },
        C3: function (c3, stageFp, stageQ, gain) {
          return c3 * gain;
        },
        R1pR2: function (parallelR1R2, stageFp, stageQ, gain) {
          let alpha = 1 / stageQ;
          let w0 = stageFp * 2 * Math.PI;
          let gx = 2 + 1 / gain;
          return 1 / (w0 * parallelR1R2 * (gx / alpha + alpha / (gain * gx)));
        },
        R1: function (r1, stageFp, stageQ, gain) {
          let alpha = 1 / stageQ;
          let w0 = stageFp * 2 * Math.PI;
          let gx = 2 + 1 / gain;
          return alpha / (w0 * r1 * gx);
        },
        R2: function (r2, stageFp, stageQ, gain) {
          let alpha = 1 / stageQ;
          let w0 = stageFp * 2 * Math.PI;
          let gx = 2 + 1 / gain;
          return (gain * gx) / (alpha * w0 * r2);
        },
      },
      bandpass: {
        // Governing component whose value is being returned: C1
        //TODO - Breakdown bandpassMFBFeedback into what each component needs
        C2: function (c2 /*, stageFp, stageQ, gain*/) {
          return c2;
        },
        R1pR2: function (parallelR1R2, stageFp, stageQ, gain) {
          let w0 = stageFp * 2 * Math.PI;
          let { q0 } = _bandpassMFBFeedback(stageQ, gain);
          return 1 / (2 * q0 * w0 * parallelR1R2);
        },
        R1: function (r1, stageFp, stageQ, gain) {
          let { b, q0 } = _bandpassMFBFeedback(stageQ, gain);
          let w0 = stageFp * 2 * Math.PI;
          return 1 / (2 * q0 * r1 * w0 * b);
        },
        R2: function (r2, stageFp, stageQ, gain) {
          let { b, q0 } = _bandpassMFBFeedback(stageQ, gain);
          let w0 = stageFp * 2 * Math.PI;
          return 1 / (1 - b) > 10e6 ? undefined : 1 / (2 * q0 * r2 * w0 * (1 - b));
        },
        R5: function (r5, stageFp, stageQ, gain) {
          let { q0 } = _bandpassMFBFeedback(stageQ, gain);
          let w0 = stageFp * 2 * Math.PI;
          return (2 * q0) / (w0 * r5);
        },
      },
    },
    sallenKey: {
      // Governing component whose value is being returned: C1
      lowpassV2: {
        C2: function (c2, stageFp, stageQ, gain) {
          let ratios = _sallenKeyLPFResAndCapRatios(gain, stageQ, 1, 'ideal');
          //capRatio is C1/C2:
          return ratios.capRatio * c2;
        },
        R1pR2: function (parallelR1R2, stageFp, stageQ, gain) {
          let ratios = _sallenKeyLPFResAndCapRatios(gain, stageQ, 1, 'ideal');
          let r1 = (parallelR1R2 * (1 + ratios.resRatio)) / ratios.resRatio;
          let r2 = r1 * ratios.resRatio;
          let squareRootOfC1C2 = 1 / (2 * Math.PI * Math.sqrt(r1 * r2) * stageFp);
          let c1 = Math.sqrt(ratios.capRatio * squareRootOfC1C2 * squareRootOfC1C2);
          return c1;
        },
        R1: function (r1, stageFp, stageQ, gain) {
          //Get rough draft
          let ratios = _sallenKeyLPFResAndCapRatios(gain, stageQ, 1, 'ideal');
          let r2 = r1 * ratios.resRatio;
          let squareRootOfC1C2 = 1 / (2 * Math.PI * Math.sqrt(r1 * r2) * stageFp);
          let c1 = Math.sqrt(ratios.capRatio * squareRootOfC1C2 * squareRootOfC1C2);
          return c1;
        },
        R2: function (r2, stageFp, stageQ, gain) {
          let ratios = _sallenKeyLPFResAndCapRatios(gain, stageQ, 1, 'ideal');
          let r1 = r2 / ratios.resRatio;
          let squareRootOfC1C2 = 1 / (2 * Math.PI * Math.sqrt(r1 * r2) * stageFp);
          let c1 = Math.sqrt(ratios.capRatio * squareRootOfC1C2 * squareRootOfC1C2);
          return c1;
        },
      },
      //This was the original algorithm to calculate Sallen Key components.  It prioritized low noise gain over everything else.
      //Unfortunately it resulted wide R1/R2 spacing at low gains and low Q's: i.e. the most common settings.  For example for
      //Gain=1, Q=0.707, R2/R1 = 17.9.
      //This wide spacing meant that in order for a customer to have decent input impedance (high enough R1), then R2 had to be quite
      //large, resulting in a worse noise result than if we had slightly worse noise gain, but closer resistor spacing.
      lowpass: {
        // Governing component whose value is being returned: C1
        C2: function (c2, stageFp, stageQ, gain) {
          if (stageQ * stageQ * (gain * gain - 8 * gain + 16) < 2 || gain > 4) {
            return c2;
          } else if (13 * stageQ - 10 * stageQ * gain > Math.sqrt(20)) {
            let a = 4;
            let b = 4 * (1 - gain) - 1 / (stageQ * stageQ);
            let d = (1 - gain) * (1 - gain);
            let capacitorRatio = (-b + Math.sqrt(b * b - 4 * a * d)) / (2 * a);
            return c2 / capacitorRatio;
          } else {
            return c2 * 10;
          }
        },
        R1pR2: function (parallelR1R2, stageFp, stageQ, gain) {
          if (stageQ * stageQ * (gain * gain - 8 * gain + 16) < 2 || gain > 4) {
            let a = 1;
            let b = 2 * (2 - gain) - 1 / (stageQ * stageQ);
            let d = (2 - gain) * (2 - gain);
            let resistorRatio = (-b + Math.sqrt(b * b - 4 * a * d)) / (2 * a);
            let sq = Math.sqrt(resistorRatio);
            return 1 / (2 * Math.PI * stageFp * parallelR1R2 * (sq + 1 / sq));
          } else if (13 * stageQ - 10 * stageQ * gain > Math.sqrt(20)) {
            let a = 4;
            let b = 4 * (1 - gain) - 1 / (stageQ * stageQ);
            let d = (1 - gain) * (1 - gain);
            let capacitorRatio = (-b + Math.sqrt(b * b - 4 * a * d)) / (2 * a);
            return 1 / (4 * Math.PI * stageFp * parallelR1R2 * Math.sqrt(capacitorRatio));
          } else {
            let a = 1;
            let b = 2 * (11 - 10 * gain) - 10 / (stageQ * stageQ);
            let d = (11 - 10 * gain) * (11 - 10 * gain);
            let resistorRatio = (-b + Math.sqrt(b * b - 4 * a * d)) / (2 * a);
            let sq = Math.sqrt(resistorRatio);
            return 1 / (2 * Math.PI * stageFp * parallelR1R2 * Math.sqrt(1 / 10) * (sq + 1 / sq));
          }
        },
        R1: function (r1, stageFp, stageQ, gain) {
          if (stageQ * stageQ * (gain * gain - 8 * gain + 16) < 2 || gain > 4) {
            let a = 1;
            let b = 2 * (2 - gain) - 1 / (stageQ * stageQ);
            let d = (2 - gain) * (2 - gain);
            let resistorRatio = (-b + Math.sqrt(b * b - 4 * a * d)) / (2 * a);
            let rMultAvg = 1 / (2 * Math.PI * stageFp * r1);
            return rMultAvg / Math.sqrt(resistorRatio);
          } else if (13 * stageQ - 10 * stageQ * gain > Math.sqrt(20)) {
            let a = 4;
            let b = 4 * (1 - gain) - 1 / (stageQ * stageQ);
            let d = (1 - gain) * (1 - gain);
            let capacitorRatio = (-b + Math.sqrt(b * b - 4 * a * d)) / (2 * a);
            return 1 / (2 * Math.PI * stageFp * r1 * Math.sqrt(capacitorRatio));
          } else {
            let a = 1;
            let b = 2 * (11 - 10 * gain) - 10 / (stageQ * stageQ);
            let d = (11 - 10 * gain) * (11 - 10 * gain);
            let resistorRatio = (-b + Math.sqrt(b * b - 4 * a * d)) / (2 * a);
            let rMultAvg = 1 / (2 * Math.PI * stageFp * r1 * Math.sqrt(0.1));
            return rMultAvg / Math.sqrt(resistorRatio);
          }
        },
        R2: function (r2, stageFp, stageQ, gain) {
          if (stageQ * stageQ * (gain * gain - 8 * gain + 16) < 2 || gain > 4) {
            let a = 1;
            let b = 2 * (2 - gain) - 1 / (stageQ * stageQ);
            let d = (2 - gain) * (2 - gain);
            let resistorRatio = (-b + Math.sqrt(b * b - 4 * a * d)) / (2 * a);
            let rMultAvg = 1 / (2 * Math.PI * stageFp * r2);
            return rMultAvg * Math.sqrt(resistorRatio);
          } else if (13 * stageQ - 10 * stageQ * gain > Math.sqrt(20)) {
            let a = 4;
            let b = 4 * (1 - gain) - 1 / (stageQ * stageQ);
            let d = (1 - gain) * (1 - gain);
            let capacitorRatio = (-b + Math.sqrt(b * b - 4 * a * d)) / (2 * a);
            return 1 / (2 * Math.PI * stageFp * r2 * Math.sqrt(capacitorRatio));
          } else {
            let a = 1;
            let b = 2 * (11 - 10 * gain) - 10 / (stageQ * stageQ);
            let d = (11 - 10 * gain) * (11 - 10 * gain);
            let resistorRatio = (-b + Math.sqrt(b * b - 4 * a * d)) / (2 * a);
            let rMultAvg = 1 / (2 * Math.PI * stageFp * r2 * Math.sqrt(0.1));
            return rMultAvg * Math.sqrt(resistorRatio);
          }
        },
      },
      highpass: {
        // Governing component whose value is being returned: C1
        C2: function (c2 /*, stageFp, stageQ, gain, rGain*/) {
          return c2;
        },
        R1pR2: function (parallelR1R2, stageFp, stageQ, gain) {
          var k = gain < 1.0001 ? 1.0001 : gain;
          var a = stageQ * stageQ * (1 - k) * (1 - k);
          var b = 4 * stageQ * stageQ * (1 - k) - 1;
          var c = 4 * stageQ * stageQ;
          let resistorRatio = (-b - Math.sqrt(b * b - 4 * a * c)) / (2 * a);
          return 1 / (2 * Math.PI * stageFp * parallelR1R2 * (Math.sqrt(resistorRatio) + 1 / Math.sqrt(resistorRatio)));
        },
        R1: function (r1, stageFp, stageQ, gain) {
          var k = gain < 1.0001 ? 1.0001 : gain;
          var a = stageQ * stageQ * (1 - k) * (1 - k);
          var b = 4 * stageQ * stageQ * (1 - k) - 1;
          var c = 4 * stageQ * stageQ;
          let resistorRatio = (-b - Math.sqrt(b * b - 4 * a * c)) / (2 * a);
          let resistorBaseValue = 1 / (2 * Math.PI * stageFp * r1);
          return resistorBaseValue / Math.sqrt(resistorRatio);
        },
        R2: function (r2, stageFp, stageQ, gain) {
          var k = gain < 1.0001 ? 1.0001 : gain;
          var a = stageQ * stageQ * (1 - k) * (1 - k);
          var b = 4 * stageQ * stageQ * (1 - k) - 1;
          var c = 4 * stageQ * stageQ;
          let resistorRatio = (-b - Math.sqrt(b * b - 4 * a * c)) / (2 * a);
          let resistorBaseValue = 1 / (2 * Math.PI * stageFp * r2);
          return resistorBaseValue * Math.sqrt(resistorRatio);
        },
      },
    },
  },
  differential: {
    passiveFirstOrder: {
      lowpass: {
        grounded: {
          // Governing component whose value is being returned: C1 and C2
          R1: function (r1, stageFp, _stageQ, gain, crossedValue) {
            let equivC = 1 / (stageFp * 2 * Math.PI * 2 * r1);
            return 2 * (equivC - crossedValue);
          },
        },
        crossed: {
          // Governing component whose value is being returned: C3
          R1: function (r1, stageFp, _stageQ, gain, groundedValue) {
            let equivC = 1 / (stageFp * 2 * Math.PI * 2 * r1);
            return equivC - groundedValue / 2;
          },
        },
      },
      highpass: {
        grounded: {
          // Governing component whose value is being returned: R1 and R2
          C1: function (c1, stageFp, stageQ, gain, crossedValue) {
            let equivR = 2 / (stageFp * 2 * Math.PI * c1);
            return -1 / (2 / crossedValue - 2 / equivR);
          },
        },
        crossed: {
          // Governing component whose value is being returned: R3
          C1: function (c1, stageFp, stageQ, gain, groundedValue) {
            let equivR = 2 / (stageFp * 2 * Math.PI * c1);
            return -1 / (1 / (2 * groundedValue) - 1 / equivR);
          },
        },
      },
    },
    activeFirstOrder: {
      lowpass: {
        grounded: {
          // Governing component whose value is being returned: C1 and C2
          R1: function (r1, stageFp, _stageQ, gain) {
            return 1 / (stageFp * 2 * Math.PI * r1 * Math.abs(gain));
          },
          R2: function (r2, stageFp /*, stageQ, gain*/) {
            return 1 / (stageFp * 2 * Math.PI * r2);
          },
        },
        crossed: {
          // Governing component whose value is being returned: NA
          R1: function () {
            return undefined;
          },
          R2: function () {
            return undefined;
          },
        },
      },
      highpass: {
        grounded: {
          // Governing component whose value is being returned: C1 and C2
          R1: function (r1, stageFp /*, stageQ, gain*/) {
            return 1 / (stageFp * 2 * Math.PI * r1);
          },
          R2: function (r2, stageFp, _stageQ, gain) {
            return Math.abs(gain) / (stageFp * 2 * Math.PI * r2);
          },
        },
        crossed: {
          // Governing component whose value is being returned: NA
          R1: function () {
            return undefined;
          },
          R2: function () {
            return undefined;
          },
        },
      },
    },
    multipleFeedback: {
      lowpass: {
        grounded: {
          // Governing component whose value is being returned: C2 and C12
          C1: function (c1, stageFp, stageQ, gain) {
            let alpha = 1 / stageQ;
            return c1 / ((4 / (alpha * alpha)) * (gain + 1));
          },
          R2pR5: function (parallelR2R5, stageFp, stageQ, gain) {
            let w0 = stageFp * 2 * Math.PI;
            let alpha = 1 / stageQ;
            return alpha / (2 * w0 * parallelR2R5 * (gain + 2));
          },
          R1: function (r1, stageFp, stageQ, gain) {
            let w0 = stageFp * 2 * Math.PI;
            let alpha = 1 / stageQ;
            return alpha / (2 * gain * w0 * r1);
          },
          R2: function (r2, stageFp, stageQ, gain) {
            let w0 = stageFp * 2 * Math.PI;
            let alpha = 1 / stageQ;
            return alpha / (2 * (gain + 1) * w0 * r2);
          },
          R5: function (r5, stageFp, stageQ /*, gain*/) {
            let w0 = stageFp * 2 * Math.PI;
            let alpha = 1 / stageQ;
            return alpha / (2 * w0 * r5);
          },
        },
        crossed: {
          // Governing component whose value is being returned: NA
          C1: function () {
            return undefined;
          },
          R2pR5: function () {
            return undefined;
          },
          R1: function () {
            return undefined;
          },
          R2: function () {
            return undefined;
          },
          R5: function () {
            return undefined;
          },
        },
      },
      highpass: {
        grounded: {
          // Governing component whose value is being returned: C1 and C11
          C2: function (c2 /*, stageFp, stageQ, gain*/) {
            return c2;
          },
          C3: function (c3, stageFp, stageQ, gain) {
            return c3 * gain;
          },
          R1pR2: function (parallelR1R2, stageFp, stageQ, gain) {
            let alpha = 1 / stageQ;
            let w0 = stageFp * 2 * Math.PI;
            let gx = 2 + 1 / gain;
            return 1 / (w0 * parallelR1R2 * (gx / alpha + alpha / (gain * gx)));
          },
          R1: function (r1, stageFp, stageQ, gain) {
            let alpha = 1 / stageQ;
            let w0 = stageFp * 2 * Math.PI;
            let gx = 2 + 1 / gain;
            return alpha / (w0 * r1 * gx);
          },
          R2: function (r2, stageFp, stageQ, gain) {
            let alpha = 1 / stageQ;
            let w0 = stageFp * 2 * Math.PI;
            let gx = 2 + 1 / gain;
            return (gain * gx) / (alpha * w0 * r2);
          },
        },
        crossed: {
          // Governing component whose value is being returned: NA
          C2: function () {
            return undefined;
          },
          C3: function () {
            return undefined;
          },
          R1pR2: function () {
            return undefined;
          },
          R1: function () {
            return undefined;
          },
          R2: function () {
            return undefined;
          },
        },
      },
      bandpass: {
        grounded: {
          // Governing component whose value is being returned: C1 and C11
          C2: function (c2 /*, stageFp, stageQ, gain*/) {
            return c2;
          },
          R1pR2: function (parallelR1R2, stageFp, stageQ, gain) {
            let w0 = stageFp * 2 * Math.PI;
            let { q0 } = _bandpassMFBFeedback(stageQ, gain);
            return 1 / (2 * q0 * w0 * parallelR1R2);
          },
          R1: function (r1, stageFp, stageQ, gain) {
            let { b, q0 } = _bandpassMFBFeedback(stageQ, gain);
            let w0 = stageFp * 2 * Math.PI;
            return 1 / (2 * q0 * r1 * w0 * b);
          },
          R2: function (r2, stageFp, stageQ, gain) {
            let { b, q0 } = _bandpassMFBFeedback(stageQ, gain);
            let w0 = stageFp * 2 * Math.PI;
            return 1 / (1 - b) > 10e6 ? undefined : 1 / (2 * q0 * r2 * w0 * (1 - b));
          },
          R5: function (r5, stageFp, stageQ, gain) {
            let { q0 } = _bandpassMFBFeedback(stageQ, gain);
            let w0 = stageFp * 2 * Math.PI;
            return (2 * q0) / (w0 * r5);
          },
        },
        //TODO - Breakdown bandpassMFBFeedback into what each component needs
        crossed: {
          // Governing component whose value is being returned: NA
          C2: function () {
            return undefined;
          },
          R1pR2: function () {
            return undefined;
          },
          R1: function () {
            return undefined;
          },
          R2: function () {
            return undefined;
          },
          R5: function () {
            return undefined;
          },
        },
      },
    },
    passiveInductor: {
      lowpass: {
        grounded: {
          // Governing component whose value is being returned: C1 and C2
          R1: function (r1, stageFp, stageQ, gain, crossedValue) {
            let w0 = stageFp * 2 * Math.PI;
            let equivC = 1 / (w0 * stageQ * 2 * r1);
            return 2 * (equivC - crossedValue);
          },
          L1: function (l1, stageFp, stageQ, gain, crossedValue) {
            let w0 = stageFp * 2 * Math.PI;
            let equivC = 1 / (w0 * w0 * 2 * l1);
            return 2 * (equivC - crossedValue);
          },
        },
        crossed: {
          // Governing component whose value is being returned: C3
          R1: function (r1, stageFp, stageQ, gain, groundedValue) {
            let w0 = stageFp * 2 * Math.PI;
            let equivC = 1 / (w0 * stageQ * 2 * r1);
            return equivC - groundedValue / 2;
          },
          L1: function (l1, stageFp, stageQ, gain, groundedValue) {
            let w0 = stageFp * 2 * Math.PI;
            let equivC = 1 / (w0 * w0 * 2 * l1);
            return equivC - groundedValue / 2;
          },
        },
      },
      bandpass: {
        grounded: {
          // Governing component whose value is being returned: C1 and C2
          R1: function (r1, stageFp, stageQ, gain, crossedValue) {
            let w0 = stageFp * 2 * Math.PI;
            let equivC = stageQ / (w0 * 2 * r1);
            return 2 * (equivC - crossedValue);
          },
          L3: function (l3, stageFp, _stageQ, gain, crossedValue) {
            let w0 = stageFp * 2 * Math.PI;
            let equivC = 1 / (w0 * w0 * l3);
            return 2 * (equivC - crossedValue);
          },
        },
        crossed: {
          // Governing component whose value is being returned: C3
          R1: function (r1, stageFp, stageQ, gain, groundedValue) {
            let w0 = stageFp * 2 * Math.PI;
            let equivC = stageQ / (w0 * 2 * r1);
            return equivC - groundedValue / 2;
          },
          L3: function (l3, stageFp, _stageQ, gain, groundedValue) {
            let w0 = stageFp * 2 * Math.PI;
            let equivC = 1 / (w0 * w0 * l3);
            return equivC - groundedValue / 2;
          },
        },
      },
    },
  },
};

//exported only for internals consistency testing - not for public consumption
export let individualComponentCalculators = {
  singleEnded: {
    passiveFirstOrder: {
      lowpass: {
        r1: function (cGov, stageFp /*,_stageQ, gain , _rGain*/) {
          return 1 / (stageFp * 2 * Math.PI * cGov);
        },
      },
      highpass: {
        r1: function (cGov, stageFp /*, stageQ, gain, rGain*/) {
          return 1 / (stageFp * 2 * Math.PI * cGov);
        },
      },
    },
    activeFirstOrder: {
      lowpass: {
        r1: function (cGov, stageFp, _stageQ, gain /*, _rGain*/) {
          let rGov = 1 / (stageFp * 2 * Math.PI * cGov);
          if (gain > 0) {
            return rGov;
          } else {
            return rGov / Math.abs(gain);
          }
        },
        r2: function (cGov, stageFp, _stageQ, gain /*, _rGain*/) {
          if (gain > 0) {
            return undefined;
          } else {
            return 1 / (stageFp * 2 * Math.PI * cGov);
          }
        },
        r3: function (cGov, stageFp, stageQ, gain, rGain) {
          return _determineR3(gain, rGain);
        },
        r4: function (cGov, stageFp, stageQ, gain, rGain) {
          return _determineR4(gain, rGain);
        },
      },
      highpass: {
        r1: function (cGov, stageFp /*, stageQ, gain, rGain*/) {
          return 1 / (stageFp * 2 * Math.PI * cGov);
        },
        r2: function (cGov, stageFp, _stageQ, gain /*, _rGain*/) {
          if (gain > 0) {
            return undefined;
          } else {
            return Math.abs(gain) / (stageFp * 2 * Math.PI * cGov);
          }
        },
        r3: function (cGov, stageFp, stageQ, gain, rGain) {
          return _determineR3(gain, rGain);
        },
        r4: function (cGov, stageFp, stageQ, gain, rGain) {
          return _determineR4(gain, rGain);
        },
      },
    },
    activeInductor: {
      lowpass: {
        r1: function (cGov, stageFp, stageQ /*, gain, rGain*/) {
          let w0 = stageFp * 2 * Math.PI;
          return 1 / (w0 * stageQ * cGov);
        },
        r3: function (cGov, stageFp, stageQ, gain, rGain) {
          return _determineR3(gain, rGain);
        },
        r4: function (cGov, stageFp, stageQ, gain, rGain) {
          return _determineR4(gain, rGain);
        },
        l1: function (cGov, stageFp /*, stageQ, gain, rGain*/) {
          let w0 = stageFp * 2 * Math.PI;
          return 1 / (w0 * w0 * cGov);
        },
      },
      // highpass: {
      // r1: function (cGov, stageFp, stageQ, gain, rGain) {
      //     let w0 = stageFp * 2 * Math.PI;
      //     return 1 / (w0 * stageQ * cGov);
      // },
      // r3: function (cGov, stageFp, stageQ, gain, rGain) {
      //     return determineR3(gain, rGain);
      // },
      // r4: function (cGov, stageFp, stageQ, gain, rGain) {
      //     return determineR4(gain, rGain);
      // },
      // l1: function (cGov, stageFp, stageQ, gain, rGain) {
      //     let w0 = stageFp * 2 * Math.PI;
      //     return 1 / (w0 * w0 * cGov);
      // }
      // }
      bandpass: {
        r1: function (cGov, stageFp, stageQ /*, gain, rGain*/) {
          let w0 = stageFp * 2 * Math.PI;
          return stageQ / (w0 * cGov);
        },
        r3: function (cGov, stageFp, stageQ, gain, rGain) {
          return _determineR3(gain, rGain);
        },
        r4: function (cGov, stageFp, stageQ, gain, rGain) {
          return _determineR4(gain, rGain);
        },
        l1: function (cGov, stageFp /*, stageQ, gain, rGain*/) {
          let w0 = stageFp * 2 * Math.PI;
          return 1 / (w0 * w0 * cGov);
        },
      },
    },
    multipleFeedback: {
      lowpass: {
        c1: function (cGov, stageFp, stageQ, gain /*, rGain*/) {
          let alpha = 1 / stageQ;
          return (4 / (alpha * alpha)) * (gain + 1) * cGov;
        },
        r1: function (cGov, stageFp, stageQ, gain /*, rGain*/) {
          let w0 = stageFp * 2 * Math.PI;
          let alpha = 1 / stageQ;
          let kVar = w0 * cGov;
          return alpha / (2 * gain * kVar);
        },
        r2: function (cGov, stageFp, stageQ, gain /*, rGain*/) {
          let w0 = stageFp * 2 * Math.PI;
          let alpha = 1 / stageQ;
          let kVar = w0 * cGov;
          return alpha / (2 * (gain + 1) * kVar);
        },
        r3: function (/*cGov, stageFp, stageQ, gain, rGain*/) {
          return 0;
        },
        r4: function (/*cGov, stageFp, stageQ, gain, rGain*/) {
          return INFINITY;
        },
        r5: function (cGov, stageFp, stageQ /*, gain, rGain*/) {
          let w0 = stageFp * 2 * Math.PI;
          let alpha = 1 / stageQ;
          let kVar = w0 * cGov;
          return alpha / (2 * kVar);
        },
      },
      highpass: {
        c2: function (cGov /*, stageFp, stageQ, gain, rGain*/) {
          return cGov;
        },
        c3: function (cGov, stageFp, stageQ, gain /*, rGain*/) {
          return cGov / gain;
        },
        r1: function (cGov, stageFp, stageQ, gain /*, rGain*/) {
          let alpha = 1 / stageQ;
          let kVar = stageFp * 2 * Math.PI * cGov;
          return alpha / (kVar * (2 + 1 / gain));
        },
        r2: function (cGov, stageFp, stageQ, gain /*, rGain*/) {
          let alpha = 1 / stageQ;
          let kVar = stageFp * 2 * Math.PI * cGov;
          return (gain * (2 + 1 / gain)) / (alpha * kVar);
        },
        r3: function (/*cGov, stageFp, stageQ, gain, rGain*/) {
          return 0;
        },
        r4: function (/*cGov, stageFp, stageQ, gain, rGain*/) {
          return INFINITY;
        },
      },
      bandpass: {
        //TODO - Breakdown bandpassMFBFeedback into what each component needs
        c2: function (cGov /*, stageFp, stageQ, gain, rGain*/) {
          return cGov;
        },
        r1: function (cGov, stageFp, stageQ, gain /*, rGain*/) {
          let { b, q0 } = _bandpassMFBFeedback(stageQ, gain);
          let w0 = stageFp * 2 * Math.PI;
          let r1PlusR2 = 1 / (2 * q0 * cGov * w0);
          return r1PlusR2 / b;
        },
        r2: function (cGov, stageFp, stageQ, gain /*, rGain*/) {
          let { b, q0 } = _bandpassMFBFeedback(stageQ, gain);
          let w0 = stageFp * 2 * Math.PI;
          let r1PlusR2 = 1 / (2 * q0 * cGov * w0);
          let r2 = r1PlusR2 / (1 - b);
          r2 = r2 > 1e11 ? INFINITY : r2;
          return r2;
        },
        r3: function (cGov, stageFp, stageQ, gain, rGain) {
          let { g } = _bandpassMFBFeedback(stageQ, gain);
          return _determineR3(g, rGain);
        },
        r4: function (cGov, stageFp, stageQ, gain, rGain) {
          let { g } = _bandpassMFBFeedback(stageQ, gain);
          return _determineR4(g, rGain);
        },
        r5: function (cGov, stageFp, stageQ, gain /*, rGain*/) {
          let { q0 } = _bandpassMFBFeedback(stageQ, gain);
          let w0 = stageFp * 2 * Math.PI;
          return (2 * q0) / w0 / cGov;
        },
      },
    },
    passiveInductor: {
      lowpass: {
        r1: function (cGov, stageFp, stageQ /*, gain, rGain*/) {
          let w0 = stageFp * 2 * Math.PI;
          return 1 / (w0 * stageQ * cGov);
        },
        l1: function (cGov, stageFp /*, stageQ, gain, rGain*/) {
          let w0 = stageFp * 2 * Math.PI;
          return 1 / (w0 * w0 * cGov);
        },
      },
      // highpass: {
      // r1: function (cGov, stageFp, stageQ, gain, rGain) {
      //     let w0 = stageFp * 2 * Math.PI;
      //     return 1 / (w0 * stageQ * cGov);
      // },
      // r3: function (cGov, stageFp, stageQ, gain, rGain) {
      //     return determineR3(gain, rGain);
      // },
      // r4: function (cGov, stageFp, stageQ, gain, rGain) {
      //     return determineR4(gain, rGain);
      // },
      // l1: function (cGov, stageFp, stageQ, gain, rGain) {
      //     let w0 = stageFp * 2 * Math.PI;
      //     return 1 / (w0 * w0 * cGov);
      // }
      // }
      bandpass: {
        r1: function (cGov, stageFp, stageQ /*, gain, rGain*/) {
          let w0 = stageFp * 2 * Math.PI;
          return stageQ / (w0 * cGov);
        },
        l1: function (cGov, stageFp /*, stageQ, gain, rGain*/) {
          let w0 = stageFp * 2 * Math.PI;
          return 1 / (w0 * w0 * cGov);
        },
      },
    },
    sallenKey: {
      lowpassV2: {
        c2: function (cGov, stageFp, stageQ, gain, capESeries /*, rGain*/) {
          let ratios = _sallenKeyLPFResAndCapRatios(gain, stageQ, 1, capESeries);
          //capRatio = C1/C2
          return cGov / ratios.capRatio;
        },
        r1: function (cGov, stageFp, stageQ, gain, capESeries /*, rGain*/) {
          let ratios = _sallenKeyLPFResAndCapRatios(gain, stageQ, 1, capESeries);
          let c1 = cGov;
          let c2 = cGov / ratios.capRatio;
          let squareRootOfR1R2 = 1 / (2 * Math.PI * Math.sqrt(c1 * c2) * stageFp);
          let r2 = Math.sqrt(ratios.resRatio * squareRootOfR1R2 * squareRootOfR1R2);
          let r1 = r2 / ratios.resRatio;
          return r1;
        },
        r2: function (cGov, stageFp, stageQ, gain, capESeries /*, rGain*/) {
          let ratios = _sallenKeyLPFResAndCapRatios(gain, stageQ, 1, capESeries);
          let c1 = cGov;
          let c2 = cGov / ratios.capRatio;
          let squareRootOfR1R2 = 1 / (2 * Math.PI * Math.sqrt(c1 * c2) * stageFp);
          let r2 = Math.sqrt(ratios.resRatio * squareRootOfR1R2 * squareRootOfR1R2);
          return r2;
        },
        r3: function (cGov, stageFp, stageQ, gain, rGain) {
          return _determineR3(gain, rGain);
        },
        r4: function (cGov, stageFp, stageQ, gain, rGain) {
          return _determineR4(gain, rGain);
        },
      },
      //This was the original algorithm to calculate Sallen Key components.  It prioritized low noise gain over everything else.
      //Unfortunately it resulted wide R1/R2 spacing at low gains and low Q's: i.e. the most common settings.  For example for
      //Gain=1, Q=0.707, R2/R1 = 17.9.
      //This wide spacing meant that in order for a customer to have decent input impedance (high enough R1), then R2 had to be quite
      //large, resulting in a worse noise result than if we had slightly worse noise gain, but closer resistor spacing.
      lowpass: {
        c2: function (cGov, stageFp, stageQ, gain /*, rGain*/) {
          if (stageQ * stageQ * (gain * gain - 8 * gain + 16) < 2 || gain > 4) {
            return cGov;
          } else if (13 * stageQ - 10 * stageQ * gain > Math.sqrt(20)) {
            let a = 4;
            let b = 4 * (1 - gain) - 1 / (stageQ * stageQ);
            let d = (1 - gain) * (1 - gain);
            let capacitorRatio = (-b + Math.sqrt(b * b - 4 * a * d)) / (2 * a);
            return cGov * capacitorRatio;
          } else {
            return cGov / 10;
          }
        },
        r1: function (cGov, stageFp, stageQ, gain /*, rGain*/) {
          if (stageQ * stageQ * (gain * gain - 8 * gain + 16) < 2 || gain > 4) {
            let a = 1;
            let b = 2 * (2 - gain) - 1 / (stageQ * stageQ);
            let d = (2 - gain) * (2 - gain);
            let resistorRatio = (-b + Math.sqrt(b * b - 4 * a * d)) / (2 * a);
            let rMultAvg = 1 / (2 * Math.PI * stageFp * cGov);
            return rMultAvg / Math.sqrt(resistorRatio);
          } else if (13 * stageQ - 10 * stageQ * gain > Math.sqrt(20)) {
            let a = 4;
            let b = 4 * (1 - gain) - 1 / (stageQ * stageQ);
            let d = (1 - gain) * (1 - gain);
            let capacitorRatio = (-b + Math.sqrt(b * b - 4 * a * d)) / (2 * a);
            return 1 / (2 * Math.PI * stageFp * cGov * Math.sqrt(capacitorRatio));
          } else {
            let a = 1;
            let b = 2 * (11 - 10 * gain) - 10 / (stageQ * stageQ);
            let d = (11 - 10 * gain) * (11 - 10 * gain);
            let resistorRatio = (-b + Math.sqrt(b * b - 4 * a * d)) / (2 * a);
            let rMultAvg = 1 / (2 * Math.PI * stageFp * cGov * Math.sqrt(0.1));
            return rMultAvg / Math.sqrt(resistorRatio);
          }
        },
        r2: function (cGov, stageFp, stageQ, gain /*, rGain*/) {
          if (stageQ * stageQ * (gain * gain - 8 * gain + 16) < 2 || gain > 4) {
            let a = 1;
            let b = 2 * (2 - gain) - 1 / (stageQ * stageQ);
            let d = (2 - gain) * (2 - gain);
            let resistorRatio = (-b + Math.sqrt(b * b - 4 * a * d)) / (2 * a);
            let rMultAvg = 1 / (2 * Math.PI * stageFp * cGov);
            return rMultAvg * Math.sqrt(resistorRatio);
          } else if (13 * stageQ - 10 * stageQ * gain > Math.sqrt(20)) {
            let a = 4;
            let b = 4 * (1 - gain) - 1 / (stageQ * stageQ);
            let d = (1 - gain) * (1 - gain);
            let capacitorRatio = (-b + Math.sqrt(b * b - 4 * a * d)) / (2 * a);
            return 1 / (2 * Math.PI * stageFp * cGov * Math.sqrt(capacitorRatio));
          } else {
            let a = 1;
            let b = 2 * (11 - 10 * gain) - 10 / (stageQ * stageQ);
            let d = (11 - 10 * gain) * (11 - 10 * gain);
            let resistorRatio = (-b + Math.sqrt(b * b - 4 * a * d)) / (2 * a);
            let rMultAvg = 1 / (2 * Math.PI * stageFp * cGov * Math.sqrt(0.1));
            return rMultAvg * Math.sqrt(resistorRatio);
          }
        },
        r3: function (cGov, stageFp, stageQ, gain, rGain) {
          return _determineR3(gain, rGain);
        },
        r4: function (cGov, stageFp, stageQ, gain, rGain) {
          return _determineR4(gain, rGain);
        },
      },
      highpass: {
        c2: function (cGov /*, stageFp, stageQ, gain, rGain*/) {
          return cGov;
        },
        r1: function (cGov, stageFp, stageQ, gain /*, rGain*/) {
          var k = gain < 1.0001 ? 1.0001 : gain;
          var a = stageQ * stageQ * (1 - k) * (1 - k);
          var b = 4 * stageQ * stageQ * (1 - k) - 1;
          var c = 4 * stageQ * stageQ;
          let resistorRatio = (-b - Math.sqrt(b * b - 4 * a * c)) / (2 * a);
          let resistorBaseValue = 1 / (2 * Math.PI * stageFp * cGov);
          return resistorBaseValue / Math.sqrt(resistorRatio);
        },
        r2: function (cGov, stageFp, stageQ, gain /*, rGain*/) {
          var k = gain < 1.0001 ? 1.0001 : gain;
          var a = stageQ * stageQ * (1 - k) * (1 - k);
          var b = 4 * stageQ * stageQ * (1 - k) - 1;
          var c = 4 * stageQ * stageQ;
          let resistorRatio = (-b - Math.sqrt(b * b - 4 * a * c)) / (2 * a);
          let resistorBaseValue = 1 / (2 * Math.PI * stageFp * cGov);
          return resistorBaseValue * Math.sqrt(resistorRatio);
        },
        r3: function (cGov, stageFp, stageQ, gain, rGain) {
          return _determineR3(gain, rGain);
        },
        r4: function (cGov, stageFp, stageQ, gain, rGain) {
          return _determineR4(gain, rGain);
        },
      },
    },
  },
  differential: {
    passiveFirstOrder: {
      lowpass: {
        r1: function (cGov, stageFp, stageQ, gain, rGainOrCCross) {
          let equivC = cGov / 2 + rGainOrCCross;
          return 1 / (2 * Math.PI * stageFp * 2 * equivC);
        },
        r2: function (cGov, stageFp, stageQ, gain, rGainOrCCross) {
          let equivC = cGov / 2 + rGainOrCCross;
          return 1 / (2 * Math.PI * stageFp * 2 * equivC);
        },
      },
      highpass: {
        c1: function (rGov, stageFp, stageQ, gain, rGainOrRCross) {
          let equivR = 1 / (1 / (2 * rGov) + 1 / rGainOrRCross);
          return 2 / (2 * Math.PI * stageFp * equivR);
        },
        c2: function (rGov, stageFp, stageQ, gain, rGainOrRCross) {
          let equivR = 1 / (1 / (2 * rGov) + 1 / rGainOrRCross);
          return 2 / (2 * Math.PI * stageFp * equivR);
        },
      },
    },
    activeFirstOrder: {
      lowpass: {
        r1: function (cGov, stageFp, _stageQ, gain) {
          return 1 / (2 * Math.PI * stageFp * cGov * gain);
        },
        r2: function (cGov, stageFp /*, _stageQ, gain*/) {
          return 1 / (2 * Math.PI * stageFp * cGov);
        },
        r3: function (cGov, stageFp, _stageQ, gain) {
          return 1 / (2 * Math.PI * stageFp * cGov * gain);
        },
        r4: function (cGov, stageFp /*, _stageQ, gain*/) {
          return 1 / (2 * Math.PI * stageFp * cGov);
        },
      },
      highpass: {
        r1: function (cGov, stageFp /*, _stageQ , gain*/) {
          return 1 / (2 * Math.PI * stageFp * cGov);
        },
        r2: function (cGov, stageFp, _stageQ, gain) {
          return gain / (2 * Math.PI * stageFp * cGov);
        },
        r3: function (cGov, stageFp /*, _stageQ, gain*/) {
          return 1 / (2 * Math.PI * stageFp * cGov);
        },
        r4: function (cGov, stageFp, _stageQ, gain) {
          return gain / (2 * Math.PI * stageFp * cGov);
        },
      },
    },
    multipleFeedback: {
      lowpass: {
        c1: function (cGov, stageFp, stageQ, gain /*, rGain*/) {
          let alpha = 1 / stageQ;
          return (4 / (alpha * alpha)) * (gain + 1) * cGov;
        },
        r1: function (cGov, stageFp, stageQ, gain /*, rGain*/) {
          let w0 = stageFp * 2 * Math.PI;
          let alpha = 1 / stageQ;
          let kVar = w0 * cGov;
          return alpha / (2 * gain * kVar);
        },
        r2: function (cGov, stageFp, stageQ, gain /*, rGain*/) {
          let w0 = stageFp * 2 * Math.PI;
          let alpha = 1 / stageQ;
          let kVar = w0 * cGov;
          return alpha / (2 * (gain + 1) * kVar);
        },
        r5: function (cGov, stageFp, stageQ /*, gain, rGain*/) {
          let w0 = stageFp * 2 * Math.PI;
          let alpha = 1 / stageQ;
          let kVar = w0 * cGov;
          return alpha / (2 * kVar);
        },
        c11: function (cGov, stageFp, stageQ, gain /*, rGain*/) {
          let alpha = 1 / stageQ;
          return (4 / (alpha * alpha)) * (gain + 1) * cGov;
        },
        r11: function (cGov, stageFp, stageQ, gain /*, rGain*/) {
          let w0 = stageFp * 2 * Math.PI;
          let alpha = 1 / stageQ;
          let kVar = w0 * cGov;
          return alpha / (2 * gain * kVar);
        },
        r12: function (cGov, stageFp, stageQ, gain /*, rGain*/) {
          let w0 = stageFp * 2 * Math.PI;
          let alpha = 1 / stageQ;
          let kVar = w0 * cGov;
          return alpha / (2 * (gain + 1) * kVar);
        },
        r15: function (cGov, stageFp, stageQ /*, gain, rGain*/) {
          let w0 = stageFp * 2 * Math.PI;
          let alpha = 1 / stageQ;
          let kVar = w0 * cGov;
          return alpha / (2 * kVar);
        },
      },
      highpass: {
        c2: function (cGov /*, stageFp, stageQ, gain, rGain*/) {
          return cGov;
        },
        c3: function (cGov, stageFp, stageQ, gain /*, rGain*/) {
          return cGov / gain;
        },
        r1: function (cGov, stageFp, stageQ, gain /*, rGain*/) {
          let alpha = 1 / stageQ;
          let kVar = stageFp * 2 * Math.PI * cGov;
          return alpha / (kVar * (2 + 1 / gain));
        },
        r2: function (cGov, stageFp, stageQ, gain /*, rGain*/) {
          let alpha = 1 / stageQ;
          let kVar = stageFp * 2 * Math.PI * cGov;
          return (gain * (2 + 1 / gain)) / (alpha * kVar);
        },
        c12: function (cGov /*, stageFp, stageQ, gain, rGain*/) {
          return cGov;
        },
        c13: function (cGov, stageFp, stageQ, gain /*, rGain*/) {
          return cGov / gain;
        },
        r11: function (cGov, stageFp, stageQ, gain /*, rGain*/) {
          let alpha = 1 / stageQ;
          let kVar = stageFp * 2 * Math.PI * cGov;
          return alpha / (kVar * (2 + 1 / gain));
        },
        r12: function (cGov, stageFp, stageQ, gain /*, rGain*/) {
          let alpha = 1 / stageQ;
          let kVar = stageFp * 2 * Math.PI * cGov;
          return (gain * (2 + 1 / gain)) / (alpha * kVar);
        },
      },
      bandpass: {
        //TODO - Breakdown bandpassMFBFeedback into what each component needs
        c2: function (cGov /*, stageFp, stageQ, gain, rGain*/) {
          return cGov;
        },
        r1: function (cGov, stageFp, stageQ, gain /*, rGain*/) {
          let { b, q0 } = _bandpassMFBFeedback(stageQ, gain);
          let w0 = stageFp * 2 * Math.PI;
          let r1PlusR2 = 1 / (2 * q0 * cGov * w0);
          return r1PlusR2 / b;
        },
        r2: function (cGov, stageFp, stageQ, gain /*, rGain*/) {
          let { b, q0 } = _bandpassMFBFeedback(stageQ, gain);
          let w0 = stageFp * 2 * Math.PI;
          let r1PlusR2 = 1 / (2 * q0 * cGov * w0);
          let r2 = r1PlusR2 / (1 - b);
          r2 = r2 > 1e11 ? INFINITY : r2;
          return r2;
        },
        r5: function (cGov, stageFp, stageQ, gain /*, rGain*/) {
          let { q0 } = _bandpassMFBFeedback(stageQ, gain);
          let w0 = stageFp * 2 * Math.PI;
          return (2 * q0) / w0 / cGov;
        },
        c12: function (cGov /*, stageFp, stageQ, gain, rGain*/) {
          return cGov;
        },
        r11: function (cGov, stageFp, stageQ, gain /*, rGain*/) {
          let { b, q0 } = _bandpassMFBFeedback(stageQ, gain);
          let w0 = stageFp * 2 * Math.PI;
          let r1PlusR2 = 1 / (2 * q0 * cGov * w0);
          return r1PlusR2 / b;
        },
        r12: function (cGov, stageFp, stageQ, gain /*, rGain*/) {
          let { b, q0 } = _bandpassMFBFeedback(stageQ, gain);
          let w0 = stageFp * 2 * Math.PI;
          let r1PlusR2 = 1 / (2 * q0 * cGov * w0);
          let r2 = r1PlusR2 / (1 - b);
          r2 = r2 > 1e11 ? INFINITY : r2;
          return r2;
        },
        r15: function (cGov, stageFp, stageQ, gain /*, rGain*/) {
          let { q0 } = _bandpassMFBFeedback(stageQ, gain);
          let w0 = stageFp * 2 * Math.PI;
          return (2 * q0) / w0 / cGov;
        },
      },
    },
    passiveInductor: {
      lowpass: {
        r1: function (cGov, stageFp, stageQ, gain, rGainOrCCross) {
          let w0 = stageFp * 2 * Math.PI;
          let equivC = cGov / 2 + rGainOrCCross;
          return 1 / (w0 * stageQ * 2 * equivC);
        },
        r2: function (cGov, stageFp, stageQ, gain, rGainOrCCross) {
          let w0 = stageFp * 2 * Math.PI;
          let equivC = cGov / 2 + rGainOrCCross;
          return 1 / (w0 * stageQ * 2 * equivC);
        },
        l1: function (cGov, stageFp, stageQ, gain, rGainOrCCross) {
          let w0 = stageFp * 2 * Math.PI;
          let equivC = cGov / 2 + rGainOrCCross;
          return 1 / (w0 * w0 * 2 * equivC);
        },
        l2: function (cGov, stageFp, stageQ, gain, rGainOrCCross) {
          let w0 = stageFp * 2 * Math.PI;
          let equivC = cGov / 2 + rGainOrCCross;
          return 1 / (w0 * w0 * 2 * equivC);
        },
      },
      // highpass: {
      // r1: function (cGov, stageFp, stageQ, gain, rGain) {
      //     let w0 = stageFp * 2 * Math.PI;
      //     return 1 / (w0 * stageQ * cGov);
      // },
      // r3: function (cGov, stageFp, stageQ, gain, rGain) {
      //     return determineR3(gain, rGain);
      // },
      // r4: function (cGov, stageFp, stageQ, gain, rGain) {
      //     return determineR4(gain, rGain);
      // },
      // l1: function (cGov, stageFp, stageQ, gain, rGain) {
      //     let w0 = stageFp * 2 * Math.PI;
      //     return 1 / (w0 * w0 * cGov);
      // }
      // }
      bandpass: {
        r1: function (cGov, stageFp, stageQ, gain, rGainOrCCross) {
          let w0 = stageFp * 2 * Math.PI;
          let equivC = cGov / 2 + rGainOrCCross;
          return stageQ / (w0 * 2 * equivC);
        },
        r2: function (cGov, stageFp, stageQ, gain, rGainOrCCross) {
          let w0 = stageFp * 2 * Math.PI;
          let equivC = cGov / 2 + rGainOrCCross;
          return stageQ / (w0 * 2 * equivC);
        },
        l3: function (cGov, stageFp, stageQ, gain, rGainOrCCross) {
          let w0 = stageFp * 2 * Math.PI;
          let equivC = cGov / 2 + rGainOrCCross;
          return 1 / (w0 * w0 * equivC);
        },
      },
    },
  },
};

export let componentCalculators = {
  singleEnded: {
    passiveFirstOrder: {
      lowpass: function (cGov, stageFp /*, stageQ, gain, rGainOrCCross*/) {
        let rGov = 1 / (stageFp * 2 * Math.PI * cGov);
        return { c1: cGov, r1: rGov };
      },
      highpass: function (cGov, stageFp /*, stageQ, gain, rGainOrCCross*/) {
        let rGov = 1 / (stageFp * 2 * Math.PI * cGov);
        return { c1: cGov, r1: rGov };
      },
    },
    activeFirstOrder: {
      lowpass: function (cGov, stageFp, stageQ, gain, rGainOrCCross) {
        let rGov = 1 / (stageFp * 2 * Math.PI * cGov);
        if (gain > 0) {
          let { r3, r4 } = _determineGainResistors(rGainOrCCross, gain);
          return { c1: cGov, r1: rGov, r3, r4 };
        } else {
          return { c1: cGov, r1: rGov / Math.abs(gain), r2: rGov };
        }
      },
      highpass: function (cGov, stageFp, stageQ, gain, rGainOrCCross) {
        let rGov = 1 / (stageFp * 2 * Math.PI * cGov);
        if (gain > 0) {
          let { r3, r4 } = _determineGainResistors(rGainOrCCross, gain);
          return { c1: cGov, r1: rGov, r3, r4 };
        } else {
          return { c1: cGov, r1: rGov, r2: rGov * Math.abs(gain) };
        }
      },
    },
    passiveInductor: {
      lowpass: function (cGov, stageFp, stageQ /*, gain, rGainOrCCross*/) {
        let w0 = stageFp * 2 * Math.PI;
        let r1 = 1 / (w0 * stageQ * cGov);
        let l1 = 1 / (w0 * w0 * cGov);
        return { c1: cGov, r1, l1 };
      },
      // highpass: function (cGov, stageFp, stageQ, gain, rGainOrCCross) {
      //     let w0 = stageFp * 2 * Math.PI;
      //     let r1 = 1 / (w0 * stageQ * cGov);
      //     let l1 = 1 / (w0 * w0 * cGov)
      //     return {c1: cGov, r1, l1};
      // },
      bandpass: function (cGov, stageFp, stageQ /*, gain, rGainOrCCross*/) {
        let w0 = stageFp * 2 * Math.PI;
        let r1 = stageQ / (w0 * cGov);
        let l1 = 1 / (w0 * w0 * cGov);
        return { c1: cGov, r1, l1 };
      },
    },
    activeInductor: {
      lowpass: function (cGov, stageFp, stageQ, gain, rGainOrCCross) {
        let { r3, r4 } = _determineGainResistors(rGainOrCCross, gain);
        let w0 = stageFp * 2 * Math.PI;
        let r1 = 1 / (w0 * stageQ * cGov);
        let l1 = 1 / (w0 * w0 * cGov);
        return { c1: cGov, r1, r3, r4, l1 };
      },
      // highpass: function (cGov, stageFp, stageQ, gain, rGain) {
      //     let w0 = stageFp * 2 * Math.PI;
      //     let r1 = 1 / (w0 * stageQ * cGov);
      //     let l1 = 1 / (w0 * w0 * cGov)
      //     return {c1: cGov, r1, l1};
      // },
      bandpass: function (cGov, stageFp, stageQ, gain, rGainOrCCross) {
        let { r3, r4 } = _determineGainResistors(rGainOrCCross, gain);
        let w0 = stageFp * 2 * Math.PI;
        let r1 = stageQ / (w0 * cGov);
        let l1 = 1 / (w0 * w0 * cGov);
        return { c1: cGov, r1, r3, r4, l1 };
      },
    },
    multipleFeedback: {
      // Weirdness - cGov for this circuit is c2, FW implementation starts with initial value and uses E24 to bias all the values.
      lowpass: function (cGov, stageFp, stageQ, gain /*, rGainOrCCross*/) {
        let { r3, r4 } = { r3: 0, r4: INFINITY };
        let w0 = stageFp * 2 * Math.PI;
        let alpha = 1 / stageQ;
        let kVar = w0 * cGov;
        let c1 = (4 / (alpha * alpha)) * (gain + 1) * cGov;
        let r1 = alpha / (2 * gain * kVar);
        let r2 = alpha / (2 * (gain + 1) * kVar);
        let r5 = alpha / (2 * kVar);
        return { c1, c2: cGov, r1, r2, r3, r4, r5 };
      },
      highpass: function (cGov, stageFp, stageQ, gain /*, rGainOrCCross*/) {
        let { r3, r4 } = { r3: 0, r4: INFINITY };
        let c2 = cGov;
        let c3 = cGov / gain;

        let alpha = 1 / stageQ;
        let kVar = stageFp * 2 * Math.PI * cGov;

        let r1 = alpha / (kVar * (2 + 1 / gain));
        let r2 = (gain * (2 + 1 / gain)) / (alpha * kVar);
        return { c1: cGov, c2, c3, r1, r2, r3, r4 };
      },
      bandpass: function (cGov, stageFp, stageQ, gain, rGainOrCCross) {
        let { b, q0, g } = _bandpassMFBFeedback(stageQ, gain);
        let { r3, r4 } = _determineGainResistors(rGainOrCCross, g);
        let w0 = stageFp * 2 * Math.PI;
        let r1PlusR2 = 1 / (2 * q0 * cGov * w0);
        let c2 = cGov;
        let r1 = r1PlusR2 / b;
        let r2 = r1PlusR2 / (1 - b);
        let r5 = (2 * q0) / (w0 * cGov);
        r2 = r2 > 1e11 ? INFINITY : r2;
        //TODO: Derive R3 and R4 from rgain & gain=1/k
        return { c1: cGov, c2, r1, r2, r3, r4, r5 };
      },
    },
    sallenKey: {
      highpass: function (cGov, stageFp, stageQ, gain, rGainOrCCross) {
        let { r3, r4 } = _determineGainResistors(rGainOrCCross, gain);
        var k = gain < 1.0001 ? 1.0001 : gain;
        var a = stageQ * stageQ * (1 - k) * (1 - k);
        var b = 4 * stageQ * stageQ * (1 - k) - 1;
        var c = 4 * stageQ * stageQ;
        let resistorRatio = (-b - Math.sqrt(b * b - 4 * a * c)) / (2 * a);
        let resistorBaseValue = 1 / (2 * Math.PI * stageFp * cGov);
        let c2 = cGov;
        let r1 = resistorBaseValue / Math.sqrt(resistorRatio);
        let r2 = resistorBaseValue * Math.sqrt(resistorRatio);
        return { c1: cGov, c2, r1, r2, r3, r4 };
      },
      lowpassV2: function (cGov, stageFp, stageQ, gain, rGainOrCCross, capESeries) {
        let { r3, r4 } = _determineGainResistors(rGainOrCCross, gain);
        let ratios = _sallenKeyLPFResAndCapRatios(gain, stageQ, cGov, capESeries);
        let c1 = cGov;
        let c2 = cGov / ratios.capRatio;
        let squareRootOfR1R2 = 1 / (2 * Math.PI * Math.sqrt(c1 * c2) * stageFp);
        let r2 = Math.sqrt(ratios.resRatio * squareRootOfR1R2 * squareRootOfR1R2);
        let r1 = r2 / ratios.resRatio;
        return { c1, c2, r1, r2, r3, r4 };
      },
      //This was the original algorithm to calculate Sallen Key components.  It prioritized low noise gain over everything else.
      //Unfortunately it resulted wide R1/R2 spacing at low gains and low Q's: i.e. the most common settings.  For example for
      //Gain=1, Q=0.707, R2/R1 = 17.9.
      //This wide spacing meant that in order for a customer to have decent input impedance (high enough R1), then R2 had to be quite
      //large, resulting in a worse noise result than if we had slightly worse noise gain, but closer resistor spacing.
      lowpass: function (cGov, stageFp, stageQ, gain, rGainOrCCross) {
        let { r3, r4 } = _determineGainResistors(rGainOrCCross, gain);
        if (stageQ * stageQ * (gain * gain - 8 * gain + 16) < 2 || gain > 4) {
          //This case covers when result.r1/result.r2 < 1/2 with caps equal.   In this case we'll set caps equal.
          let a = 1;
          let b = 2 * (2 - gain) - 1 / (stageQ * stageQ);
          let d = (2 - gain) * (2 - gain);
          let resistorRatio = (-b + Math.sqrt(b * b - 4 * a * d)) / (2 * a);
          let rMultAvg = 1 / (2 * Math.PI * stageFp * cGov);
          let c2 = cGov;
          let r1 = rMultAvg / Math.sqrt(resistorRatio);
          let r2 = rMultAvg * Math.sqrt(resistorRatio);
          return { c1: cGov, c2, r1, r2, r3, r4 };
        } else if (13 * stageQ - 10 * stageQ * gain > Math.sqrt(20)) {
          // else if result.r1/result.r2 > 1/2 with result.c1 = 10xC2, then set result.r1=result.r2
          let a = 4;
          let b = 4 * (1 - gain) - 1 / (stageQ * stageQ);
          let d = (1 - gain) * (1 - gain);
          let capacitorRatio = (-b + Math.sqrt(b * b - 4 * a * d)) / (2 * a);
          let c2 = cGov * capacitorRatio;
          let r2 = 1 / (2 * Math.PI * stageFp * cGov * Math.sqrt(capacitorRatio));
          let r1 = r2;
          return { c1: cGov, c2, r1, r2, r3, r4 };
        } else {
          // if the other two conditions are not met, this means result.r1/result.r2 should be less 1/2 with result.c1 = 10xC2
          let a = 1;
          let b = 2 * (11 - 10 * gain) - 10 / (stageQ * stageQ);
          let d = (11 - 10 * gain) * (11 - 10 * gain);
          let resistorRatio = (-b + Math.sqrt(b * b - 4 * a * d)) / (2 * a);
          let rMultAvg = 1 / (2 * Math.PI * stageFp * cGov * Math.sqrt(0.1));
          let c2 = cGov / 10;
          let r1 = rMultAvg / Math.sqrt(resistorRatio);
          let r2 = rMultAvg * Math.sqrt(resistorRatio);
          return { c1: cGov, c2, r1, r2, r3, r4 };
        }
      },
    },
  },
  differential: {
    passiveFirstOrder: {
      lowpass: function (cGov, stageFp, stageQ, gain, rGainOrCCross) {
        let equivC = cGov / 2 + rGainOrCCross;
        let rGov = 1 / (stageFp * 2 * Math.PI * 2 * equivC);
        return { c1: cGov, c2: cGov, c3: rGainOrCCross, r1: rGov, r2: rGov };
      },
      highpass: function (rGov, stageFp, stageQ, gain, rGainOrRCross) {
        // Confusing naming - rGov and rGainOrRCross passed in would be the function's cGov and rGainOrCCross
        let equivR = 1 / (1 / (2 * rGov) + 1 / rGainOrRCross);
        let cGov = 2 / (stageFp * 2 * Math.PI * equivR);
        return { c1: cGov, c2: cGov, r1: rGov, r2: rGov, r3: rGainOrRCross };
      },
    },
    activeFirstOrder: {
      lowpass: function (cGov, stageFp, stageQ, gain /*, rGainOrCCross*/) {
        let rGov = 1 / (stageFp * 2 * Math.PI * cGov);
        return { c1: cGov, c2: cGov, r1: rGov / gain, r2: rGov, r3: rGov / gain, r4: rGov };
      },
      highpass: function (cGov, stageFp, stageQ, gain /*rGainOrCCross*/) {
        let rGov = 1 / (stageFp * 2 * Math.PI * cGov);
        return { c1: cGov, c2: cGov, r1: rGov, r2: rGov * gain, r3: rGov, r4: rGov * gain };
      },
    },
    multipleFeedback: {
      // Weirdness - cGov for this circuit is c2, FW implementation starts with initial value and uses E24 to bias all the values.
      lowpass: function (cGov, stageFp, stageQ, gain /*, rGainOrCCross*/) {
        let w0 = stageFp * 2 * Math.PI;
        let alpha = 1 / stageQ;
        let kVar = w0 * cGov;
        let c1 = (4 / (alpha * alpha)) * (gain + 1) * cGov;
        let r1 = alpha / (2 * gain * kVar);
        let r2 = alpha / (2 * (gain + 1) * kVar);
        let r5 = alpha / (2 * kVar);
        return { c1, c2: cGov, c11: c1, c12: cGov, r1, r2, r5, r11: r1, r12: r2, r15: r5 };
      },
      highpass: function (cGov, stageFp, stageQ, gain /*, rGainOrCCross*/) {
        let c2 = cGov;
        let c3 = cGov / gain;

        let alpha = 1 / stageQ;
        let kVar = stageFp * 2 * Math.PI * cGov;

        let r1 = alpha / (kVar * (2 + 1 / gain));
        let r2 = (gain * (2 + 1 / gain)) / (alpha * kVar);
        return { c1: cGov, c2, c3, c11: cGov, c12: c2, c13: c3, r1, r2, r11: r1, r12: r2 };
      },
      bandpass: function (cGov, stageFp, stageQ, gain /*, rGainOrCCross*/) {
        let { b, q0 } = _bandpassMFBFeedback(stageQ, gain);
        let w0 = stageFp * 2 * Math.PI;
        let r1PlusR2 = 1 / (2 * q0 * cGov * w0);
        let c2 = cGov;
        let r1 = r1PlusR2 / b;
        let r2 = r1PlusR2 / (1 - b);
        let r5 = (2 * q0) / (w0 * cGov);
        r2 = r2 > 1e11 ? INFINITY : r2;
        //TODO: Derive R3 and R4 from rgain & gain=1/k
        return { c1: cGov, c2, c11: cGov, c12: c2, r1, r2, r5, r11: r1, r12: r2, r15: r5 };
      },
    },
    passiveInductor: {
      lowpass: function (cGov, stageFp, stageQ, gain, rGainOrCCross) {
        let w0 = stageFp * 2 * Math.PI;
        let equivC = cGov / 2 + rGainOrCCross;
        let rGov = 1 / (w0 * stageQ * 2 * equivC);
        let lGov = 1 / (w0 * w0 * 2 * equivC);
        return { c1: cGov, c2: cGov, c3: rGainOrCCross, r1: rGov, r2: rGov, l1: lGov, l2: lGov };
      },
      // highpass: function (cGov, stageFp, stageQ, gain, rGainOrCCross) {
      //     let w0 = stageFp * 2 * Math.PI;
      //     let r1 = 1 / (w0 * stageQ * cGov);
      //     let l1 = 1 / (w0 * w0 * cGov)
      //     return {c1: cGov, r1, l1};
      // },
      bandpass: function (cGov, stageFp, stageQ, gain, rGainOrCCross) {
        let w0 = stageFp * 2 * Math.PI;
        let equivC = cGov / 2 + rGainOrCCross;
        let rGov = stageQ / (w0 * 2 * equivC);
        let lGov = 1 / (w0 * w0 * equivC);
        return { c1: cGov, c2: cGov, c3: rGainOrCCross, r1: rGov, r2: rGov, l3: lGov };
      },
    },
  },
};

export let specsCalculatorsFromComponents = {
  singleEnded: {
    activeInductor: {
      lowpass: function (filterComponents /*, opAmpGBW*/) {
        if (!filterComponents) {
          return;
        }
        if (filterComponents.r1 && filterComponents.c1 && filterComponents.l1) {
          const r3 = filterComponents.r3 ?? 1e66;
          const r4 = filterComponents.r4 ?? 0;
          return {
            order: 2,
            fp: Math.sqrt(1 / (filterComponents.c1 * filterComponents.l1)) / (Math.PI * 2), //pole frequency
            q: (1 / filterComponents.r1) * Math.sqrt(filterComponents.l1 / filterComponents.c1), //peaking
            fz: 0, //zero frequency
            gain: 1 + r4 / r3, //in V/V
          };
        }
        return;
      },
      highpass: function (filterComponents /*, opAmpGBW*/) {
        if (!filterComponents) {
          return;
        }
        if (filterComponents.r1 && filterComponents.c1 && filterComponents.l1) {
          const r3 = filterComponents.r3 ?? 1e66;
          const r4 = filterComponents.r4 ?? 0;
          return {
            order: 2,
            fp: Math.sqrt(1 / (filterComponents.c1 * filterComponents.l1)) / (Math.PI * 2), //pole frequency
            q: (1 / filterComponents.r1) * Math.sqrt(filterComponents.l1 / filterComponents.c1), //peaking
            fz: 0, //zero frequency
            gain: 1 + r4 / r3, //in V/V
          };
        }
        return;
      },
      bandpass: function (filterComponents /*, opAmpGBW*/) {
        if (!filterComponents) {
          return;
        }
        if (filterComponents.r1 && filterComponents.c1 && filterComponents.l1) {
          const r3 = filterComponents.r3 ?? 1e66;
          const r4 = filterComponents.r4 ?? 0;
          return {
            order: 2,
            fp: Math.sqrt(1 / (filterComponents.c1 * filterComponents.l1)) / (Math.PI * 2), //pole frequency
            q: filterComponents.r1 * Math.sqrt(filterComponents.c1 / filterComponents.l1), //peaking
            fz: 0, //zero frequency
            gain: 1 + r4 / r3, //in V/V
          };
        }
        return;
      },
    },
    activeFirstOrder: {
      lowpass: function (filterComponents /*, opAmpGBW*/) {
        if (!filterComponents) {
          return;
        }
        if (filterComponents.r1 && filterComponents.c1) {
          const r3 = filterComponents.r3 ?? 1e66;
          const r4 = filterComponents.r4 ?? 0;
          return {
            order: 1,
            fp: 1 / (Math.PI * 2 * filterComponents.r1 * filterComponents.c1), //pole frequency
            q: 0, //peaking
            fz: 0, //zero frequency
            gain: 1 + r4 / r3, //in V/V
          };
        }
        return;
      },
      highpass: function (filterComponents /*, opAmpGBW*/) {
        if (!filterComponents) {
          return;
        }
        if (filterComponents.r1 && filterComponents.c1) {
          const r3 = filterComponents.r3 ?? 1e66;
          const r4 = filterComponents.r4 ?? 0;
          return {
            order: 1,
            fp: 1 / (Math.PI * 2 * filterComponents.r1 * filterComponents.c1), //pole frequency
            q: 0, //peaking
            fz: 0, //zero frequency
            gain: 1 + r4 / r3, //in V/V
          };
        }
        return;
      },
    },
    multipleFeedback: {
      lowpass: function (filterComponents, opAmpGBW) {
        if (
          filterComponents.c1 &&
          filterComponents.c2 &&
          filterComponents.r1 &&
          filterComponents.r2 &&
          filterComponents.r5
        ) {
          const gain = filterComponents.r5 / filterComponents.r1;
          const sqrtTerm = Math.sqrt(
            filterComponents.r2 * filterComponents.r5 * filterComponents.c1 * filterComponents.c2
          );
          const gbwMod = Math.sqrt(
            1 + (gain * gain) / (2 * Math.PI * filterComponents.r2 * filterComponents.c2 * opAmpGBW)
          );
          return {
            order: 2,
            fp: 1 / (2 * Math.PI * sqrtTerm) / gbwMod, //pole frequency
            q:
              (1 /
                (((1 / filterComponents.c1) *
                  (1 / filterComponents.r1 + 1 / filterComponents.r2 + 1 / filterComponents.r5)) /
                  (1 / sqrtTerm))) *
              gbwMod, //peaking
            fz: 0, //zero frequency
            gain: gain, //in V/V
          };
        }
        return;
      },
      highpass: function (filterComponents /*, opAmpGBW*/) {
        if (
          filterComponents.c1 &&
          filterComponents.c2 &&
          filterComponents.c3 &&
          filterComponents.r1 &&
          filterComponents.r2
        ) {
          const sqrtTerm = Math.sqrt(
            filterComponents.r1 * filterComponents.r2 * filterComponents.c2 * filterComponents.c3
          );
          return {
            order: 2,
            fp: 1 / (2 * Math.PI * sqrtTerm), //pole frequency
            q:
              1 /
              ((filterComponents.c1 + filterComponents.c2 + filterComponents.c3) /
                (filterComponents.c2 * filterComponents.c3 * filterComponents.r2 * (1 / sqrtTerm))), //peaking
            fz: 0, //zero frequency
            gain: filterComponents.c1 / filterComponents.c3, //in V/V
          };
        }
        return;
      },
      bandpass: function (filterComponents, opAmpGBW) {
        if (
          filterComponents.c1 &&
          filterComponents.c2 &&
          filterComponents.r1 &&
          filterComponents.r2 &&
          filterComponents.r5
        ) {
          const r3 = filterComponents.r3 ?? 0;
          const r4 = filterComponents.r4 ?? 1e66;
          const resOne = 1 / (1 / filterComponents.r1 + 1 / filterComponents.r2);
          const resTwo = filterComponents.r5;
          const w0 = 1 / Math.sqrt(resOne * resTwo * filterComponents.c1 * filterComponents.c2);
          const fp = w0 / 2 / Math.PI;
          const q0 = (1 / 2) * Math.sqrt(resTwo / resOne);
          const a = resOne / filterComponents.r1;
          const gain0 = ((1 / 2) * a * resTwo) / resOne;
          let q;
          let gain;
          let k;
          let alpha;
          if (r4 > 1e20) {
            q = q0;
            gain = gain0;
            k = 0;
          } else {
            k = r3 / (r3 + r4);
            alpha = k / (1 - k);
            q = q0 / (1 - 2 * alpha * q0 * q0);
            gain = (gain0 * q) / q0 / (1 - k);
          }
          const epsilon = ((2 * q0) / (opAmpGBW / fp)) * (1 / ((1 - k) * (1 - k)));
          const errorplusOne = Math.sqrt(1 + epsilon);
          return {
            order: 2,
            fp: fp / errorplusOne, //pole frequency
            q: q / errorplusOne, //peaking
            fz: 0, //zero frequency
            gain: gain, //in V/V
          };
        }
        return;
      },
    },
    sallenKey: {
      lowpass: function (filterComponents, opAmpGBW) {
        if (!filterComponents) {
          return;
        }
        if (filterComponents.r1 && filterComponents.r2 && filterComponents.c1 && filterComponents.c2) {
          const r3 = filterComponents.r3 ?? 1e66;
          const r4 = filterComponents.r4 ?? 0;
          const gain = 1 + r4 / r3;
          const sqrtTerm = Math.sqrt(
            filterComponents.r1 * filterComponents.r2 * filterComponents.c1 * filterComponents.c2
          );
          const d1 = filterComponents.r1 * filterComponents.c2;
          const d2 = filterComponents.r2 * filterComponents.c2;
          const d3 = filterComponents.r1 * filterComponents.c1;
          const gbwMod = Math.sqrt(
            1 + (gain * gain) / (2 * Math.PI * filterComponents.r2 * filterComponents.c2 * opAmpGBW)
          );
          return {
            order: 2,
            fp: 1 / (2 * Math.PI * sqrtTerm) / gbwMod, //pole frequency
            q: (sqrtTerm / (d1 + d2 + d3 * (1 - gain))) * gbwMod, //peaking
            fz: 0, //zero frequency
            gain: gain, //in V/V
          };
        }
        return;
      },
      highpass: function (filterComponents /*, opAmpGBW*/) {
        if (!filterComponents) {
          return;
        }
        if (filterComponents.r1 && filterComponents.r2 && filterComponents.c1 && filterComponents.c2) {
          const r3 = filterComponents.r3 ?? 1e66;
          const r4 = filterComponents.r4 ?? 0;
          const gain = 1 + r4 / r3;
          const sqrtTerm = Math.sqrt(
            filterComponents.r1 * filterComponents.r2 * filterComponents.c1 * filterComponents.c2
          );
          const d1 = filterComponents.r1 * filterComponents.c2;
          const d2 = filterComponents.r2 * filterComponents.c2;
          const d3 = filterComponents.r1 * filterComponents.c1;
          return {
            order: 2,
            fp: 1 / (2 * Math.PI * sqrtTerm), //pole frequency
            q: sqrtTerm / (d1 + d3 + d2 * (1 - gain)), //peaking
            fz: 0, //zero frequency
            gain: gain, //in V/V
          };
        }
        return;
      },
    },
  },
  differential: {},
};

/*
let _fromR3 = function(r3, gain) {
    if (gain >= 1.0001) {
        return r3 * gain;
    } else if (gain < 1.0001 && gain > 0.9999) {
        return INFINITY; //case of no resistor
    } else {
        return;
    }
}
let _fromR4 = function(r4, gain) {
    if (gain >= 1.0001) {
        return r4 * (gain + 1);
    } else if (gain < 1.0001 && gain > 0.9999) {
        return INFINITY; //case of no resistor
    } else {
        return;
    }
}
*/
let _determineR3 = function (gain, rGain) {
  const RSMALL_COMP_LIMIT = 1;
  if (gain >= 1.0001) {
    let r3 = rGain / gain;
    if (r3 < RSMALL_COMP_LIMIT) {
      r3 = RSMALL_COMP_LIMIT;
    }
    return r3;
  } else if (gain < 1.0001 && gain > 0.9999) {
    return INFINITY; //case of no resistor
  } else {
    return;
  }
};
let _determineR4 = function (gain, rGain) {
  const RSMALL_COMP_LIMIT = 1;
  if (gain >= 1.0001) {
    let r3 = rGain / gain;
    let r4 = r3 / gain;
    //make sure r3 value stays reasonable (at least 1 ohm)
    if (r3 < RSMALL_COMP_LIMIT) {
      r4 = (r4 * RSMALL_COMP_LIMIT) / r3;
    }
    return r4;
  } else if (gain < 1.0001 && gain > 0.9999) {
    return 0;
  } else {
    //error gain should never be less than 1
    return;
  }
};

let _determineGainResistors = function (r3plusr4, gain) {
  const RSMALL_COMP_LIMIT = 1;
  var r3, r4;

  if (gain >= 1.0001) {
    r3 = r3plusr4 / gain;
    r4 = r3plusr4 - r3;
    //make sure r3 value stays reasonable (at least 1 ohm)
    if (r3 < RSMALL_COMP_LIMIT) {
      r4 = (r4 * RSMALL_COMP_LIMIT) / r3;
      r3 = RSMALL_COMP_LIMIT;
    }
  } else if (gain < 1.0001 && gain > 0.9999) {
    r3 = INFINITY; //case of no resistor
    r4 = 0;
  } else {
    //error gain should never be less than 1
    r3 = 0;
    r4 = INFINITY;
  }

  return { r3: r3, r4: r4 };
};

let _bandpassMFBFeedback = function (stageQ, gain) {
  let a = gain / (2 * stageQ * stageQ);
  if (stageQ < 10 && a < 1) {
    return { q0: stageQ, b: a, g: 0 };
  } else {
    {
      //According to Van Valkenburg textbook, this is close to an optimal at 1.5,
      //Paul Fleischer paper indicates something slightly higher might be better
      //Yet my experiments indicate higher numbers are better still for lowest tolerance issues.
      //Only reason I haven't set it higher is because the ratio between R1 and R2  gets to be really big.  Choosing 5 keeps the ratio at 100.
      var qHat = 5;
      let q0 = 0;
      if (a < 1) {
        q0 = qHat;
      } else {
        let aMax = 0.9999999999;
        q0 =
          (-(aMax + gain) + Math.sqrt((aMax + gain) * (aMax + gain) - 8 * aMax * aMax * stageQ * stageQ)) /
          (-4 * aMax * stageQ);
      }
      let alpha = (1 / (2 * q0 * q0)) * (1 - q0 / stageQ);
      let k = alpha / (1 + alpha);
      let h = gain * (q0 / stageQ) * (1 - k);
      let b = h / 2 / (q0 * q0);
      return { q0: q0, b: b, g: 1 / k };
    }
  }
};

/***************************************  Sallen Key Lowpass Formulas *********************/
//All of these formulas come from resolving the Q formula:
// Q = sqrt(R1 R2 C1 C2) / (R1C2 + R2C2 + R1C1 (1-K))
// down to something with resRatio = R2/R1, and capRatio = C1/C2
// Q = sqrt(capRatio * resRatio) / (1 + resRatio + capRatio(1-K))

//Computes Q for a Sallen Key Lowpass, given a resistor ratio (R2/R1), capacitor ratio (C1/C2), and gain (1+R4/R3)
function _sallenKeyLowPassQ(resRatio, capRatio, gain) {
  let numerator = Math.sqrt(resRatio * capRatio);
  let denominator = 1 + resRatio + capRatio * (1 - gain);
  if (denominator <= 0) {
    return INFINITY;
  } else {
    return numerator / denominator;
  }
}

//Computes resistor ratio (R2/R1) for a Sallen Key Lowpass, given a capacitor ratio (C1/C2), gain (1+R4/R3), and Q
//The ugly math comes from solving our standard formula:
// Q = sqrt(capRatio * resRatio) / (1 + resRatio + capRatio(1-K))
// for resRatio.  (I used Wolfram Alpha to do it for me.)
function _sallenKeyLowPassResistorRatio(capRatio, gain, q) {
  let capRatioSquared = capRatio * capRatio;
  let qSquared = q * q;
  let underSquareRoot = 4 * capRatioSquared * qSquared * (gain - 1) + capRatioSquared - 4 * qSquared * capRatio;
  if (underSquareRoot < 0) {
    return NaN;
  }
  let numerator = 2 * capRatio * qSquared * (gain - 1) + capRatio - 2 * qSquared + Math.sqrt(underSquareRoot);
  let denominator = 2 * qSquared;

  if (denominator <= 0) {
    return NaN;
  } else {
    return numerator / denominator;
  }
}

//Computes capacitor ratio (C1/C2) for a Sallen Key Lowpass, given a resistor ratio (R2/R1), gain (1+R4/R3), and Q
//The ugly math comes from solving our standard formula:
// Q = sqrt(capRatio * resRatio) / (1 + resRatio + capRatio(1-K))
// for capRatio.  (I used Wolfram Alpha to do it for me.)
function _sallenKeyLowPassCapacitorRatio(resRatio, gain, q) {
  let resRatioSquared = resRatio * resRatio;
  let qSquared = q * q;
  let answer;
  if (gain === 1) {
    let numerator = qSquared * (resRatioSquared + 2 * resRatio + 1);
    let denominator = resRatio;
    answer = numerator / denominator;
  } else {
    let underSquareRoot =
      4 * resRatioSquared * qSquared * (gain - 1) + resRatioSquared - 4 * qSquared * resRatio * (gain - 1);
    if (underSquareRoot < 0) {
      return NaN;
    }
    let numerator = 2 * qSquared * (gain - 1) * (resRatio + 1) + resRatio + underSquareRoot;
    let denominator = 2 * qSquared * (gain * gain - 2 * gain + 1);
    answer = numerator / denominator;
  }

  if (answer <= 0) {
    return NaN;
  } else {
    return answer;
  }
}

// Calculates a good resistor and capacitor ratio (C1/C2) for a given Q and gain
// If it's possible to get a resistor ratio between 1 & 12 when caps are equal, do that.
// If it's possible to get a resistor ration between 1 & 6 when capratio is 10, do that.
// Otherwise return solution where:
//  a) cap ratio allows for a perfect E series match
//  b) resistor ratio is close to 2
// capESeries options: 'E6' | 'E12' | 'E24' | 'E48' | 'E96', 'ideal'
function _sallenKeyLPFResAndCapRatios(gain, q, c1, capESeries) {
  //First check if we can get matched capacitors with a 1x to 12x resistor ratio
  //We let the resistor ratio go all the way up to 12x in this case because
  //C1 wants to be the same or smaller than C2 only at high gains, which is also means high sensitivity
  //Designs where C1=C2 have lower capacitor mismatch sensitivity than those where C1<C2.  That + easier
  //component sourcing for the customer with same C1&C2 justifies letting the R2/R1 ratio getting as high as 12.
  let capRatio = 1;
  //Using 0.95 rather than 1, because this the approximate lower bound of R2/R1 ratio where resistors would be same, if customer using E24 series resistors
  let qWithMatchedRs = _sallenKeyLowPassQ(0.95, capRatio, gain);
  let qWith12ResRatio = _sallenKeyLowPassQ(12, capRatio, gain);
  if (q <= qWithMatchedRs && q >= qWith12ResRatio) {
    let resRatio = _sallenKeyLowPassResistorRatio(capRatio, gain, q);
    return {
      resRatio: resRatio,
      capRatio: capRatio,
    };
  }

  //Now do the same, but with capratio of 10 and up to a resistor ratio of 6
  capRatio = 10;
  qWithMatchedRs = _sallenKeyLowPassQ(0.95, capRatio, gain);
  let qWith6ResRatio = _sallenKeyLowPassQ(6, capRatio, gain);
  if (q <= qWithMatchedRs && q >= qWith6ResRatio) {
    let resRatio = _sallenKeyLowPassResistorRatio(capRatio, gain, q);
    return {
      resRatio: resRatio,
      capRatio: capRatio,
    };
  }

  //Otherwise find a solution where resRatio is close to 2 and we can get a good capacitor E series match
  let resRatioRoughDraft = 2;
  let capRatioRoughDraft = _sallenKeyLowPassCapacitorRatio(resRatioRoughDraft, gain, q);
  if (capESeries === 'ideal') {
    return {
      resRatio: resRatioRoughDraft,
      capRatio: capRatioRoughDraft,
    };
  }
  //Assume C1 is already normalized to the preferred series
  let c2RoughDraft = c1 / capRatioRoughDraft;
  if (capESeries === undefined) {
    //Default e series matches Filter Wizard's RC slider granularity
    capESeries = 'E24';
  }
  //Figure out C2 in the ESeries that gets us closest to the desired cap ratio
  let c2 = ESeries.normalizeToPreferredSeries(c2RoughDraft, capESeries);
  capRatio = c1 / c2;
  //Now recalculate resistor ratio with the cap ratio we just found.  Resistor ratio will no longer be exactly 2 - that's fine.
  //If resistors were able to be ideal values, then our Q will be exact for the given E series capacitors.
  //Of course resistors have their own E series, but generally it's more granular than the caps, so we have a better shot at
  //getting the desired Q value.
  let resRatio = _sallenKeyLowPassResistorRatio(capRatio, gain, q);
  return {
    resRatio: resRatio,
    capRatio: capRatio,
  };
}
